import { OverlayModule } from '@angular/cdk/overlay'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  type Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

import { combineLatest, map } from 'rxjs'

import {
  FlxChatStyleDirective,
  FlxThemeModule,
  FlxWrapperStyleDirective,
} from '@flowx/angular-theme'
import {
  ChatAttachmentEntry,
  ChatConfig,
  type ChatDataTestIds,
  ChatHeaderDisplayMode,
  ChatMessage,
  ChatMessageFile,
  FlxChatComponent,
  FlxIconModule,
} from '@flowx/angular-ui-toolkit'
import {
  CHAT_FAB_NOTIFICATION_CLEAR_DELAY_MS,
  ChatDisplayType,
  type ChatFeedbackValue,
  type ChatMessageContent,
  ChatMessageType,
  type ChatSessionState,
  type ChatSourceType,
  ComponentIdentifierEnum,
  type ConversationsListPaginationState,
  type ChatMessage as CoreChatMessage,
  type PendingVoiceMessage,
  type SessionTimeGroup,
  type Suggestion,
  activeChatSessionIdStore,
  config as appConfig,
  chatStore,
  computeChatFabNotificationCount,
  conversationsListPaginationStore,
  detachChat,
  fetchDocument,
  getLastAgentSuggestions,
  getUiFlowType,
  getUrlQueryParamValue,
  groupSessionsByDate,
  initChat,
  initChatSession,
  loadChatSessionHistory,
  loadConversationsList,
  loadMoreConversations,
  localizeString,
  localizeTemplateText,
  markChatFabSeen,
  normalizeSuggestedPrompts,
  resolveMessageActionsConfig,
  setActiveChatSessionId,
  theme,
  updateUrlWithQueryParam,
} from '@flowx/core-sdk'
import {
  ThemeComponent,
  applyChatFabNotificationCustomStyles,
  calculateFillHeight,
  getComponentStyles,
} from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { handleAngularAttachments } from '../../utils/attachments-upload.utils'
import { createChatAnalytics } from '../../utils/chat-analytics.utils'
import {
  createChatMessageFeedback,
  getChatFeedbackClassNames,
} from '../../utils/chat-message-feedback.utils'
import { createConversationsSearch } from '../../utils/conversations-search.utils'
import {
  checkAndClearAngularPendingVoice,
  cleanupAudioUrlCache,
  handleAngularVoiceMessage,
  mergeAngularDisplayMessages,
  normalizeChatHistoryMessage,
  retryAngularVoice,
} from '../../utils/voice-upload.utils'
import { getBoolExpressionSignal, getContentIcon, getThemeIcon } from '../helpers'

// An optional display text with its tags resolved; an unset one stays unset.
const localizeOptional = (text: unknown): string | undefined =>
  typeof text === 'string' && text ? localizeTemplateText(text) : undefined

const chatHeaderToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHeaderFloating,
  fullscreen: ThemeComponent.ChatHeaderFullScreen,
  fill: ThemeComponent.ChatHeader,
}

const chatFabToComponentType: Record<Exclude<ChatHeaderDisplayMode, 'fill'>, ThemeComponent> = {
  floating: ThemeComponent.ChatFabFloating,
  fullscreen: ThemeComponent.ChatFabFullScreen,
}

const chatAgentMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatAgentMessageFloating,
  fullscreen: ThemeComponent.ChatAgentMessageFullScreen,
  fill: ThemeComponent.ChatAgentMessage,
}

const chatInputAreaToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputAreaFloating,
  fullscreen: ThemeComponent.ChatInputAreaFullScreen,
  fill: ThemeComponent.ChatInputArea,
}

const chatErrorMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatErrorMessageFloating,
  fullscreen: ThemeComponent.ChatErrorMessageFullScreen,
  fill: ThemeComponent.ChatErrorMessage,
}

const chatVoiceInputToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatVoiceInputFloating,
  fullscreen: ThemeComponent.ChatVoiceInputFullScreen,
  fill: ThemeComponent.ChatVoiceInput,
}

const chatUserVoiceMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserVoiceMessageFloating,
  fullscreen: ThemeComponent.ChatUserVoiceMessageFullScreen,
  fill: ThemeComponent.ChatUserVoiceMessage,
}

const chatInputFileItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputFileItemFloating,
  fullscreen: ThemeComponent.ChatInputFileItemFullScreen,
  fill: ThemeComponent.ChatInputFileItem,
}

const chatUserFileItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserFileItemFloating,
  fullscreen: ThemeComponent.ChatUserFileItemFullScreen,
  fill: ThemeComponent.ChatUserFileItem,
}

const chatMessageActionsToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatMessageActionsFloating,
  fullscreen: ThemeComponent.ChatMessageActionsFullScreen,
  fill: ThemeComponent.ChatMessageActions,
}

const chatHistorySearchToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHistorySearchFloating,
  fullscreen: ThemeComponent.ChatHistorySearchFullScreen,
  fill: ThemeComponent.ChatHistorySearch,
}

@Component({
  selector: 'flx-chat-wrapper',
  imports: [
    FlxChatComponent,
    FlxThemeModule,
    FlxIconModule,
    FlxChatStyleDirective,
    OverlayModule,
    FlxLocalizePipe,
  ],
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  templateUrl: './chat-wrapper.component.html',
  styles: `
    .flx-chat-fab {
      position: fixed;
      z-index: 99;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      cursor: pointer;
      box-shadow: none;
      background-color: transparent;

      .flx-icon {
        width: 100%;
        height: 100%;

        ::ng-deep svg {
          width: 100%;
          height: 100%;
        }
      }
    }

    .flx-chat-fab-notification {
      position: absolute;
      top: 0;
      right: 0;
      transform: translate(20%, -20%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      height: 22px;
      min-width: 22px;
      padding: 0 4px;
      border-radius: 999px;
      border-style: solid;
      border-width: var(--flxTheme-chatFabNotificationFloating-borderWidth, 1px);
      border-color: var(--flxTheme-chatFabNotificationFloating-borderColor);
      background-color: var(--flxTheme-chatFabNotificationFloating-backgroundColor);
      color: var(--flxTheme-chatFabNotificationFloating-color);
      font-family: var(--flxTheme-chatFabNotificationFloating-fontFamily);
      font-size: var(--flxTheme-chatFabNotificationFloating-fontSize);
      font-weight: var(--flxTheme-chatFabNotificationFloating-fontWeight);
      line-height: var(--flxTheme-chatFabNotificationFloating-lineHeight);
    }

    .flx-chat-fab-notification-fullscreen {
      border-width: var(
        --flxTheme-chatFabNotificationFullScreen-borderWidth,
        var(--flxTheme-chatFabNotificationFloating-borderWidth, 1px)
      );
      border-color: var(
        --flxTheme-chatFabNotificationFullScreen-borderColor,
        var(--flxTheme-chatFabNotificationFloating-borderColor)
      );
      background-color: var(
        --flxTheme-chatFabNotificationFullScreen-backgroundColor,
        var(--flxTheme-chatFabNotificationFloating-backgroundColor)
      );
      color: var(
        --flxTheme-chatFabNotificationFullScreen-color,
        var(--flxTheme-chatFabNotificationFloating-color)
      );
      font-family: var(
        --flxTheme-chatFabNotificationFullScreen-fontFamily,
        var(--flxTheme-chatFabNotificationFloating-fontFamily)
      );
      font-size: var(
        --flxTheme-chatFabNotificationFullScreen-fontSize,
        var(--flxTheme-chatFabNotificationFloating-fontSize)
      );
      font-weight: var(
        --flxTheme-chatFabNotificationFullScreen-fontWeight,
        var(--flxTheme-chatFabNotificationFloating-fontWeight)
      );
      line-height: var(
        --flxTheme-chatFabNotificationFullScreen-lineHeight,
        var(--flxTheme-chatFabNotificationFloating-lineHeight)
      );
    }

    .flx-chat {
      z-index: 100;
      transition:
        opacity 0.2s ease,
        display 0.2s allow-discrete;
    }

    .flx-chat-hidden {
      opacity: 0 !important;
      display: none !important;
    }

    @starting-style {
      flx-chat:not(.flx-chat-hidden) {
        opacity: 0;
      }
    }

    .flx-chat-fullscreen {
      position: fixed;
      inset: 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FlxChatWrapperComponent implements OnInit, OnDestroy, AfterViewInit {
  #destroyRef = inject(DestroyRef)
  #injector = inject(Injector)
  #audioUrlCache = new Map<string, string>()
  // Locally-stored timestamps for messages the backend doesn't stamp; keyed by
  // message id so the value stays stable across stream re-emits.
  #messageTimestamps = new Map<string, Date>()
  readonly #sessionGroupLabels: Record<SessionTimeGroup, string> = {
    sys_chat_time_group_today: localizeString('@@sys_chat_time_group_today'),
    sys_chat_time_group_yesterday: localizeString('@@sys_chat_time_group_yesterday'),
    sys_chat_time_group_previous_7_days: localizeString('@@sys_chat_time_group_previous_7_days'),
    sys_chat_time_group_previous_30_days: localizeString('@@sys_chat_time_group_previous_30_days'),
    sys_chat_time_group_older: localizeString('@@sys_chat_time_group_older'),
  }
  #elementRef = inject(ElementRef)

  #coreChatState = toSignal(
    combineLatest([chatStore.stream$, activeChatSessionIdStore.stream$]).pipe(
      map(([store, sessionId]: [Record<string, ChatSessionState>, string | null]) => ({
        sessionId,
        chatHistory: (sessionId ? store[sessionId]?.chatHistory : undefined) ?? [],
      }))
    ),
    { initialValue: { sessionId: null as string | null, chatHistory: [] as CoreChatMessage[] } }
  )

  readonly messageTimeGroupLabels = {
    yesterday: localizeString('@@sys_chat_time_group_yesterday'),
    days: localizeString('@@sys_chat_time_group_days'),
    weeks: localizeString('@@sys_chat_time_group_weeks'),
    month: localizeString('@@sys_chat_time_group_month'),
    months: localizeString('@@sys_chat_time_group_months'),
    year: localizeString('@@sys_chat_time_group_year'),
    years: localizeString('@@sys_chat_time_group_years'),
  }

  readonly headerTooltips = {
    newChat: localizeString('@@sys_chat_tooltip_new_chat'),
    history: localizeString('@@sys_chat_tooltip_chat_history'),
    expand: localizeString('@@sys_chat_tooltip_expand'),
    minimize: localizeString('@@sys_chat_tooltip_minimize'),
    dock: localizeString('@@sys_chat_tooltip_dock'),
    back: localizeString('@@sys_chat_tooltip_back'),
  }

  readonly CHAT_ICON_ARIA_LABELS = {
    newChat: '@@sys_a11y_chat_new',
    expand: '@@sys_a11y_chat_expand',
    minimize: '@@sys_a11y_chat_minimize',
    dock: '@@sys_a11y_chat_dock',
    fab: '@@sys_a11y_chat_fab_open',
    history: '@@sys_a11y_chat_history',
  }

  readonly voiceLabels = {
    listeningLabel: localizeString('@@sys_chat_voice_input_recording'),
    voiceMessageLabel: localizeString('@@sys_chat_voice_input_message'),
    cancelAriaLabel: localizeString('@@sys_a11y_chat_voice_input_cancel'),
    confirmAriaLabel: localizeString('@@sys_a11y_chat_voice_input_confirm'),
    deleteAriaLabel: localizeString('@@sys_a11y_chat_voice_input_delete'),
    inputPlayAriaLabel: localizeString('@@sys_a11y_chat_voice_input_play'),
    inputPauseAriaLabel: localizeString('@@sys_a11y_chat_voice_input_pause'),
    sendingLabel: localizeString('@@sys_chat_voice_message_sending'),
    messageLabel: localizeString('@@sys_chat_voice_message'),
    errorLabel: localizeString('@@sys_chat_voice_message_error'),
    messagePlayAriaLabel: localizeString('@@sys_a11y_chat_voice_message_play'),
    messagePauseAriaLabel: localizeString('@@sys_a11y_chat_voice_message_pause'),
    retryAriaLabel: localizeString('@@sys_a11y_chat_voice_message_retry'),
    progressAriaLabel: localizeString('@@sys_a11y_chat_voice_message_progress'),
  }

  readonly inputActionLabels = {
    sendAriaLabel: localizeString('@@sys_a11y_chat_submit_message'),
    sendTooltip: localizeString('@@sys_chat_submit_message'),
    stopAriaLabel: localizeString('@@sys_a11y_chat_cancel_request'),
    stopTooltip: localizeString('@@sys_chat_cancel_request'),
  }

  readonly inputDisabledTooltip = localizeString('@@sys_chat_disabled_input')

  readonly attachmentsLabels = {
    attachAriaLabel: localizeString('@@sys_a11y_chat_attach_files'),
    removeAriaLabel: localizeString('@@sys_a11y_chat_remove_file'),
    downloadAriaLabel: localizeString('@@sys_a11y_chat_download_file'),
  }

  readonly CHAT_VOICE_INPUT_ICON_ARIA_LABELS = {
    cancel: localizeString('@@sys_a11y_chat_voice_input_cancel'),
    confirm: localizeString('@@sys_a11y_chat_voice_input_confirm'),
    delete: localizeString('@@sys_a11y_chat_voice_input_delete'),
    play: localizeString('@@sys_a11y_chat_voice_input_play'),
    pause: localizeString('@@sys_a11y_chat_voice_input_pause'),
  }

  activeView = signal<'chat' | 'history'>('chat')

  containerRef = inject(ElementRef)
  templateConfig = input<any>() // TODO: update type

  // chat
  chatDisplayOptions = computed(() => this.templateConfig().displayOptions)
  chatFlowxProps = computed(() => this.chatDisplayOptions().flowxProps)

  // chat header
  chatHeaderTemplateConfig = computed(() =>
    this.templateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_HEADER
    )
  )
  chatHeaderDisplayOptions = computed(() => this.chatHeaderTemplateConfig()?.displayOptions)
  chatHeaderFlowxProps = computed(() => this.chatHeaderDisplayOptions()?.flowxProps)

  // chat messages
  chatMessagesTemplateConfig = computed(() =>
    this.templateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_MESSAGES
    )
  )
  chatMessagesDisplayOptions = computed(() => this.chatMessagesTemplateConfig()?.displayOptions)

  // chat agent
  chatAgentTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_AGENT_MESSAGE
    )
  )
  chatAgentDisplayOptions = computed(() => this.chatAgentTemplateConfig()?.displayOptions)
  chatAgentFlowxProps = computed(() => this.chatAgentDisplayOptions()?.flowxProps)

  // chat error
  chatErrorTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_ERROR_MESSAGE
    )
  )
  chatErrorDisplayOptions = computed(() => this.chatErrorTemplateConfig()?.displayOptions)
  chatErrorFlowxProps = computed(() => this.chatErrorDisplayOptions()?.flowxProps)

  // chat user
  chatUserTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_USER_MESSAGE
    )
  )
  chatUserDisplayOptions = computed(() => this.chatUserTemplateConfig()?.displayOptions)

  // chat user voice message
  chatUserVoiceMessageTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_USER_VOICE_MESSAGE
    )
  )
  chatUserVoiceMessageDisplayOptions = computed(
    () => this.chatUserVoiceMessageTemplateConfig()?.displayOptions
  )

  // chat input
  chatInputTemplateConfig = computed(() =>
    this.templateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_INPUT
    )
  )
  chatInputDisplayOptions = computed(() => this.chatInputTemplateConfig()?.displayOptions)
  chatInputFlowxProps = computed(() => this.chatInputDisplayOptions()?.flowxProps)

  showVoiceInput = computed(
    () => (this.chatInputFlowxProps()?.voiceInputEnabled as boolean) ?? false
  )

  inputDisabled: Signal<boolean>

  // chat text input (child of CHAT_INPUT)
  chatTextInputTemplateConfig = computed(() =>
    this.chatInputTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_TEXT_INPUT
    )
  )
  chatTextInputFlowxProps = computed(
    () => this.chatTextInputTemplateConfig()?.displayOptions?.flowxProps
  )

  // chat voice input (child of CHAT_INPUT)
  chatVoiceInputTemplateConfig = computed(() =>
    this.chatInputTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_VOICE_INPUT
    )
  )
  chatVoiceInputDisplayOptions = computed(() => this.chatVoiceInputTemplateConfig()?.displayOptions)

  // chat file attachments (child of CHAT_INPUT)
  chatFileAttachmentsTemplateConfig = computed(() =>
    this.chatInputTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_FILE_ATTACHMENTS
    )
  )
  chatFileAttachmentsFlowxProps = computed(
    () => this.chatFileAttachmentsTemplateConfig()?.displayOptions?.flowxProps
  )
  chatFileAttachmentsDisplayOptions = computed(
    () => this.chatFileAttachmentsTemplateConfig()?.displayOptions
  )

  // chat file item (child of CHAT_FILE_ATTACHMENTS)
  chatFileItemTemplateConfig = computed(() =>
    this.chatFileAttachmentsTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_FILE_ITEM
    )
  )
  chatFileItemDisplayOptions = computed(() => this.chatFileItemTemplateConfig()?.displayOptions)

  // chat user attached files (direct child of CHAT_MESSAGES, sibling of CHAT_USER_MESSAGE)
  chatUserAttachedFilesTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_USER_ATTACHED_FILES
    )
  )
  chatUserAttachedFilesDisplayOptions = computed(
    () => this.chatUserAttachedFilesTemplateConfig()?.displayOptions
  )

  // chat user file item (child of CHAT_USER_ATTACHED_FILES)
  chatUserFileItemTemplateConfig = computed(() =>
    this.chatUserAttachedFilesTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_USER_FILE_ITEM
    )
  )
  chatUserFileItemDisplayOptions = computed(
    () => this.chatUserFileItemTemplateConfig()?.displayOptions
  )

  // Enabled via the chat-input flag (parity with showVoiceInput).
  showFileUpload = computed(
    () => (this.chatInputFlowxProps()?.fileAttachmentsEnabled as boolean) ?? false
  )

  // Attachment props with sensible defaults when no config is present (decision 7).
  fileAccept = computed(() => (this.chatFileAttachmentsFlowxProps()?.accept as string) ?? '*/*')
  fileMaxFiles = computed(() => (this.chatFileAttachmentsFlowxProps()?.maxFiles as number) ?? 10)
  fileMaxFilesSize = computed(
    () => (this.chatFileAttachmentsFlowxProps()?.maxFilesSize as number) ?? 5000000
  )
  fileInvalidTypeError = computed(() =>
    localizeString((this.chatFileAttachmentsFlowxProps()?.invalidFileType as string) ?? '')
  )
  fileCountExceededError = computed(() =>
    localizeString((this.chatFileAttachmentsFlowxProps()?.filesExceeded as string) ?? '')
  )
  fileSizeExceededError = computed(() =>
    localizeString((this.chatFileAttachmentsFlowxProps()?.filesSizeExceeded as string) ?? '')
  )
  attachmentsDataTestId = computed(
    () => this.chatFileAttachmentsFlowxProps()?.dataTestId as string | undefined
  )
  fileItemDataTestId = computed(
    () => this.chatFileItemDisplayOptions()?.flowxProps?.dataTestId as string | undefined
  )
  userAttachedFilesDataTestId = computed(
    () => this.chatUserAttachedFilesDisplayOptions()?.flowxProps?.dataTestId as string | undefined
  )
  userFileItemDataTestId = computed(
    () => this.chatUserFileItemDisplayOptions()?.flowxProps?.dataTestId as string | undefined
  )

  // chat reasoning (child of CHAT_MESSAGES)
  chatReasoningTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_REASONING
    )
  )

  chatDataTestId = computed(() => this.chatFlowxProps()?.dataTestId as string | undefined)

  /** `data-testid` per chat sub-component config, forwarded to flx-chat. */
  dataTestIds = computed<ChatDataTestIds>(() => {
    const testId = (
      displayOptions: { flowxProps?: Record<string, unknown> } | undefined
    ): string | undefined => displayOptions?.flowxProps?.dataTestId as string | undefined

    return {
      header: testId(this.chatHeaderDisplayOptions()),
      messages: testId(this.chatMessagesDisplayOptions()),
      input: testId(this.chatInputDisplayOptions()),
      textInput: this.chatTextInputFlowxProps()?.dataTestId as string | undefined,
      voiceInput: testId(this.chatVoiceInputDisplayOptions()),
      agentMessage: testId(this.chatAgentDisplayOptions()),
      userMessage: testId(this.chatUserDisplayOptions()),
      errorMessage: testId(this.chatErrorDisplayOptions()),
      userVoiceMessage: testId(this.chatUserVoiceMessageDisplayOptions()),
      reasoning: testId(this.chatReasoningTemplateConfig()?.displayOptions),
      suggestedPrompts: this.chatSuggestedPromptsFlowxProps()?.dataTestId as string | undefined,
      history: testId(this.chatHistoryDisplayOptions()),
    }
  })

  chatMessageActionsTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_MESSAGE_ACTIONS
    )
  )
  chatMessageActionsDisplayOptions = computed(
    () => this.chatMessageActionsTemplateConfig()?.displayOptions
  )
  chatMessageActionsFlowxProps = computed(() => this.chatMessageActionsDisplayOptions()?.flowxProps)
  messageActionsConfig = computed(() =>
    this.chatMessageActionsTemplateConfig()
      ? resolveMessageActionsConfig(this.chatMessageActionsFlowxProps(), localizeString)
      : null
  )

  chatAnalytics = createChatAnalytics(() => ({
    chat: this.templateConfig(),
    textInput: this.chatTextInputTemplateConfig(),
    suggestedPrompts: this.chatSuggestedPromptsTemplateConfig(),
    voiceInput: this.chatVoiceInputTemplateConfig(),
    messageActions: this.chatMessageActionsTemplateConfig(),
  }))

  messageFeedback = createChatMessageFeedback(
    (value, reason) => this.chatAnalytics.feedbackSent(value, reason),
    (removedValue) => this.chatAnalytics.feedbackUnset(removedValue)
  )

  feedbackClassNames = computed(() => getChatFeedbackClassNames(this.chatHeaderDisplayMode()))

  // chat suggested prompts
  chatSuggestedPromptsTemplateConfig = computed(() =>
    this.chatMessagesTemplateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_SUGGESTED_PROMPTS
    )
  )
  chatSuggestedPromptsFlowxProps = computed(
    () => this.chatSuggestedPromptsTemplateConfig()?.displayOptions?.flowxProps
  )

  chatHistoryTemplateConfig = computed(() =>
    this.templateConfig().templateConfig.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_HISTORY
    )
  )
  chatHistoryDisplayOptions = computed(() => this.chatHistoryTemplateConfig()?.displayOptions)

  chatHistoryFlowxProps = computed(() => this.chatHistoryDisplayOptions()?.flowxProps)

  chatHistoryItemTemplateConfig = computed(() =>
    this.chatHistoryTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_HISTORY_ITEM
    )
  )
  chatHistoryItemDisplayOptions = computed(
    () => this.chatHistoryItemTemplateConfig()?.displayOptions
  )

  chatHistorySectionTemplateConfig = computed(() =>
    this.chatHistoryTemplateConfig()?.templateConfig?.find(
      (t) => t.componentIdentifier === ComponentIdentifierEnum.CHAT_HISTORY_SECTION
    )
  )
  chatHistorySectionDisplayOptions = computed(
    () => this.chatHistorySectionTemplateConfig()?.displayOptions
  )

  chatConfig = computed<ChatConfig>(
    () =>
      ({
        thinkingMessage: localizeTemplateText(this.chatFlowxProps()?.thinkingMessage ?? ''),
        welcomeMessage: localizeTemplateText(this.chatFlowxProps()?.welcomeMessage ?? ''),
        title: localizeTemplateText(this.chatHeaderFlowxProps()?.title ?? ''),
        subtitle: localizeTemplateText(this.chatHeaderFlowxProps()?.subtitle ?? ''),
        showSeparator: this.chatHeaderFlowxProps()?.showSeparator,
        hideNewConversation: this.chatHeaderFlowxProps()?.hideNewConversation,
        hideHistory: this.chatHeaderFlowxProps()?.hideHistory,
        showChatIcon: this.chatHeaderFlowxProps()?.showIcon,
        newChatLabel: localizeOptional(this.chatHeaderFlowxProps()?.newChatLabel),
        maxInputRows: this.chatTextInputFlowxProps()?.maxInputRows,
        inputPlaceholder: localizeTemplateText(this.chatTextInputFlowxProps()?.placeholder ?? ''),
        showAgentAvatar: this.chatAgentFlowxProps()?.showIcon,
        errorMessage: localizeTemplateText(this.chatErrorFlowxProps()?.genericError ?? ''),
        regenerateLabel: localizeTemplateText(this.chatErrorFlowxProps()?.regenerateLabel ?? ''),
        displayMode: this.chatHeaderDisplayMode(),
        showFullscreenToggle: this.canChangeDisplayMode(),
        isFullscreen: this.isMaximized(),
        suggestedPrompts: normalizeSuggestedPrompts(
          this.chatSuggestedPromptsFlowxProps()?.prompts
        ).map((p) => ({
          label: localizeTemplateText(p.label),
          prompt: p.prompt,
        })),
        suggestedPromptsTitle: localizeOptional(this.chatSuggestedPromptsFlowxProps()?.title),
        historyTitle: localizeOptional(this.chatHistoryFlowxProps()?.title),
      }) satisfies ChatConfig
  )
  chatIcons = computed(() => {
    const headerIcon = this.chatHeaderFlowxProps()?.headerIcon
    const agentIcon = this.chatAgentFlowxProps()?.agentIcon
    const sendIcon = this.chatInputFlowxProps()?.sendIcon
    return {
      headerIcon: headerIcon
        ? getContentIcon(headerIcon)
        : getThemeIcon(chatHeaderToComponentType[this.chatHeaderDisplayMode()], 'headerIcon'),
      newChatIcon: getThemeIcon(
        chatHeaderToComponentType[this.chatHeaderDisplayMode()],
        'newChatIcon'
      ),
      expandIcon: getThemeIcon(
        chatHeaderToComponentType[this.chatHeaderDisplayMode()],
        'expandIcon'
      ),
      minimizeIcon: getThemeIcon(
        chatHeaderToComponentType[this.chatHeaderDisplayMode()],
        'minimizeIcon'
      ),
      dockIcon: getThemeIcon(chatHeaderToComponentType[this.chatHeaderDisplayMode()], 'dockIcon'),
      historyIcon: getThemeIcon(
        chatHeaderToComponentType[this.chatHeaderDisplayMode()],
        'historyIcon'
      ),
      backIcon: getThemeIcon(chatHeaderToComponentType[this.chatHeaderDisplayMode()], 'backIcon'),
      fabIcon: getThemeIcon(chatFabToComponentType[this.chatHeaderDisplayMode()], 'fabChatIcon'),
      agentIcon: agentIcon
        ? getContentIcon(agentIcon)
        : getThemeIcon(chatAgentMessageToComponentType[this.chatHeaderDisplayMode()], 'agentIcon'),
      sendIcon: sendIcon
        ? getContentIcon(sendIcon)
        : getThemeIcon(
            chatInputAreaToComponentType[this.chatHeaderDisplayMode()],
            'sendMessageIcon'
          ),
      stopIcon: getThemeIcon(
        chatInputAreaToComponentType[this.chatHeaderDisplayMode()],
        'stopResponseIcon'
      ),
      voiceMicIcon: getThemeIcon(
        chatInputAreaToComponentType[this.chatHeaderDisplayMode()],
        'voiceInputIcon'
      ),
      errorIcon: getThemeIcon(
        chatErrorMessageToComponentType[this.chatHeaderDisplayMode()],
        'errorIcon'
      ),
      voiceInputIcons: {
        cancelIcon: {
          path: getThemeIcon(
            chatVoiceInputToComponentType[this.chatHeaderDisplayMode()],
            'cancelIcon'
          ),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.cancel),
        },
        confirmIcon: {
          path: getThemeIcon(
            chatVoiceInputToComponentType[this.chatHeaderDisplayMode()],
            'confirmIcon'
          ),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.confirm),
        },
        deleteIcon: {
          path: getThemeIcon(
            chatVoiceInputToComponentType[this.chatHeaderDisplayMode()],
            'deleteIcon'
          ),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.delete),
        },
        playIcon: {
          path: getThemeIcon(
            chatVoiceInputToComponentType[this.chatHeaderDisplayMode()],
            'playIcon'
          ),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.play),
        },
        pauseIcon: {
          path: getThemeIcon(
            chatVoiceInputToComponentType[this.chatHeaderDisplayMode()],
            'pauseIcon'
          ),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.pause),
        },
      },
      voiceMessageIcons: {
        playIcon: getThemeIcon(
          chatUserVoiceMessageToComponentType[this.chatHeaderDisplayMode()],
          'playIcon'
        ),
        pauseIcon: getThemeIcon(
          chatUserVoiceMessageToComponentType[this.chatHeaderDisplayMode()],
          'pauseIcon'
        ),
        retryIcon: getThemeIcon(
          chatUserVoiceMessageToComponentType[this.chatHeaderDisplayMode()],
          'retryIcon'
        ),
      },
      attachFileIcon: getThemeIcon(
        chatInputAreaToComponentType[this.chatHeaderDisplayMode()],
        'attachFileIcon'
      ),
      fileDeleteIcon: getThemeIcon(
        chatInputFileItemToComponentType[this.chatHeaderDisplayMode()],
        'deleteIcon'
      ),
      fileDownloadIcon: getThemeIcon(
        chatUserFileItemToComponentType[this.chatHeaderDisplayMode()],
        'downloadIcon'
      ),
      messageActionsCopyIcon: getThemeIcon(
        chatMessageActionsToComponentType[this.chatHeaderDisplayMode()],
        'copyIcon'
      ),
      messageActionsCopySuccessIcon: getThemeIcon(
        chatMessageActionsToComponentType[this.chatHeaderDisplayMode()],
        'copySuccessIcon'
      ),
      messageActionsThumbUpIcon: getThemeIcon(
        chatMessageActionsToComponentType[this.chatHeaderDisplayMode()],
        'thumbUpIcon'
      ),
      messageActionsThumbUpSelectedIcon: getThemeIcon(
        chatMessageActionsToComponentType[this.chatHeaderDisplayMode()],
        'thumbUpSelectedIcon'
      ),
      messageActionsThumbDownIcon: getThemeIcon(
        chatMessageActionsToComponentType[this.chatHeaderDisplayMode()],
        'thumbDownIcon'
      ),
      messageActionsThumbDownSelectedIcon: getThemeIcon(
        chatMessageActionsToComponentType[this.chatHeaderDisplayMode()],
        'thumbDownSelectedIcon'
      ),
      searchIcon: getThemeIcon(
        chatHistorySearchToComponentType[this.chatHeaderDisplayMode()],
        'searchIcon'
      ),
      searchClearIcon: getThemeIcon(
        chatHistorySearchToComponentType[this.chatHeaderDisplayMode()],
        'clearIcon'
      ),
    }
  })

  helperTooltipClassName = computed(
    () => `${getComponentStyles(ThemeComponent.HelperTooltip, theme()) ?? ''} flx-themed`
  )

  locale = computed(() => appConfig()?.locale)

  chatHeight = signal<string | null>(null)

  fabStyles = computed(() => {
    const styles = this.chatDisplayOptions().style[this.chatHeaderDisplayMode()]?.fab
    if (!styles) {
      return {}
    }
    return {
      top: styles.position.top && `${styles.position.top}px`,
      left: styles.position.left && `${styles.position.left}px`,
      right: styles.position.right && `${styles.position.right}px`,
      bottom: styles.position.bottom && `${styles.position.bottom}px`,
      width: styles.width && `${styles.width}px`,
      height: styles.height && `${styles.height}px`,
    }
  })

  fabNotificationClassName = computed(() => {
    const isFullscreen = this.chatHeaderDisplayMode() === 'fullscreen'
    const notification =
      this.chatDisplayOptions().style[this.chatHeaderDisplayMode()]?.fab?.notification
    return [
      'flx-chat-fab-notification',
      getComponentStyles(ThemeComponent.ChatFabNotificationFloating, theme()),
      ...(isFullscreen
        ? [
            'flx-chat-fab-notification-fullscreen',
            getComponentStyles(ThemeComponent.ChatFabNotificationFullScreen, theme()),
          ]
        : []),
      applyChatFabNotificationCustomStyles(notification),
      'flx-themed',
    ]
      .filter(Boolean)
      .join(' ')
  })

  fabNotificationCount = signal(0)

  gap = computed(() => {
    const gap = this.chatDisplayOptions()?.style?.gap
    if (gap !== null && gap !== undefined) {
      return `${gap}px`
    }
    return `var(--flxTheme-chat-gap, ${this.chatHeaderDisplayMode() === 'floating' ? 'var(--flxTheme-chatFloating-gap)' : 'var(--flxTheme-chatFullScreen-gap)'})`
  })

  hideHeader = computed(
    () =>
      this.chatFlowxProps()?.hideHeader === true &&
      this.chatFlowxProps()?.displayTypes?.length &&
      this.chatFlowxProps()?.displayTypes?.[0] === ChatDisplayType.FILL
  )

  chatDisplayMode = computed<ChatDisplayType[]>(() => this.chatFlowxProps().displayTypes ?? [])
  chatOverlayMode = computed(
    () =>
      this.chatDisplayMode()?.includes(ChatDisplayType.FLOATING) ||
      this.chatDisplayMode()?.includes(ChatDisplayType.FULLSCREEN)
  )

  chatHeaderDisplayMode = computed(() => {
    const displayMode = this.chatDisplayMode()
    if (displayMode.length === 1 && displayMode[0] === ChatDisplayType.FILL) {
      return 'fill'
    }
    if (displayMode.length === 1 && displayMode[0] === ChatDisplayType.FLOATING) {
      return 'floating'
    }
    if (displayMode.length === 1 && displayMode[0] === ChatDisplayType.FULLSCREEN) {
      return 'fullscreen'
    }

    if (displayMode.length === 2) {
      return this.isMaximized() ? 'fullscreen' : 'floating'
    }

    return 'fill'
  })

  hasFloating = computed(() => this.chatDisplayMode().includes(ChatDisplayType.FLOATING))
  hasFullscreen = computed(() => this.chatDisplayMode().includes(ChatDisplayType.FULLSCREEN))
  canChangeDisplayMode = computed(() => this.hasFloating() && this.hasFullscreen())

  isMaximized = linkedSignal(
    () =>
      this.chatDisplayMode().includes(ChatDisplayType.FULLSCREEN) &&
      !this.chatDisplayMode().includes(ChatDisplayType.FLOATING)
  )
  isChatOpened = signal<boolean>(false)

  messages = signal<ChatMessage[]>([])
  filteredMessages = computed(() =>
    this.displayMessages().filter(
      (message) => !(message.type === ChatMessageType.AI_TRIGGER && message.sender === 'sender')
    )
  )
  showSuggestedPrompts = signal<boolean>(false)
  agentSuggestions = signal<Suggestion[]>([])

  reasoning = signal<boolean>(false)
  hasMoreMessages = signal<boolean>(false)
  loadingMore = signal<boolean>(false)

  // Voice state
  pendingVoice = signal<PendingVoiceMessage | null>(null)

  // File attachments state
  attachments = signal<ChatAttachmentEntry[]>([])
  attachmentsUploadError = signal<string | null>(null)

  attachIcon = computed(() => ({
    name: 'attach',
    path: this.chatIcons().attachFileIcon,
    ariaLabel: this.attachmentsLabels.attachAriaLabel,
  }))
  deleteIcon = computed(() => ({
    name: 'close',
    path: this.chatIcons().fileDeleteIcon,
    ariaLabel: this.attachmentsLabels.removeAriaLabel,
  }))
  downloadIcon = computed(() => ({
    name: 'download',
    path: this.chatIcons().fileDownloadIcon,
    ariaLabel: this.attachmentsLabels.downloadAriaLabel,
  }))

  displayMessages = computed(() =>
    mergeAngularDisplayMessages(this.messages(), this.pendingVoice(), () => this.handleRetryVoice())
  )

  chatSessions = signal<
    {
      sessionId: string
      summary: string
      messageCount: number
      active: boolean
      createdAt: string
    }[]
  >([])

  hasMoreSessions = signal<boolean>(true)
  loadingMoreSessions = signal<boolean>(false)

  conversationsSearch = createConversationsSearch(
    () => getUiFlowType()?.chatWorkflow?.resDefId,
    this.#destroyRef
  )

  readonly searchLabels = {
    placeholder: localizeString('@@sys_chat_conversations_search_placeholder'),
    clearAriaLabel: localizeString('@@sys_a11y_conversations_search_clear'),
    noResults: localizeString('@@sys_chat_conversations_no_results'),
  }

  // While the first search page loads, suppress the no-results text.
  historyEmptyStateText = computed(() =>
    this.conversationsSearch.isSearchActive()
      ? this.conversationsSearch.searchLoading()
        ? ''
        : this.searchLabels.noResults
      : localizeString('@@sys_chat_conversations_empty_state')
  )

  groupedChatSessions = computed(() =>
    groupSessionsByDate(
      this.conversationsSearch.isSearchActive()
        ? this.conversationsSearch.searchSessions()
        : this.chatSessions(),
      this.#sessionGroupLabels
    )
  )

  // Search results are unpaginated — scroll never loads more while searching.
  historyHasMoreSessions = computed(() =>
    this.conversationsSearch.isSearchActive() ? false : this.hasMoreSessions()
  )
  historyLoadingMoreSessions = computed(() =>
    this.conversationsSearch.isSearchActive()
      ? this.conversationsSearch.searchLoading()
      : this.loadingMoreSessions()
  )

  loadMoreMessages: (() => Promise<void>) | null = null
  handleSendMessage: (message: string, messageContent?: ChatMessageContent) => void
  handleStopResponse: (() => Promise<void>) | null = null

  constructor() {
    effect(() => {
      const { sessionId, chatHistory } = this.#coreChatState()
      if (!sessionId) {
        this.fabNotificationCount.set(0)
        return
      }
      if (this.isChatOpened() || !this.chatOverlayMode()) {
        markChatFabSeen(sessionId, chatHistory)
        this.fabNotificationCount.set(0)
        return
      }
      this.fabNotificationCount.set(computeChatFabNotificationCount(sessionId, chatHistory))
    })

    // Failsafe: ensure the badge clears at most 1000ms after the chat opens.
    effect((onCleanup) => {
      if (!this.isChatOpened()) {
        return
      }
      const timeout = setTimeout(
        () => this.fabNotificationCount.set(0),
        CHAT_FAB_NOTIFICATION_CLEAR_DELAY_MS
      )
      onCleanup(() => clearTimeout(timeout))
    })
  }

  handleSendSuggestedPrompt = (prompt: { label: string; prompt: string }): void => {
    this.handleSendMessage(prompt.label, {
      type: ChatMessageType.SUGGESTED_PROMPT,
      label: prompt.label,
      prompt: prompt.prompt || '',
    })
    this.chatAnalytics.agentPromptSelected()
  }

  handleTextMessage(message: string): void {
    this.handleSendMessage(message)
    this.chatAnalytics.textMessageSent()
  }

  handleFabClick(): void {
    const opening = !this.isChatOpened()
    this.isChatOpened.set(opening)
    // A chat opened from the FAB counts, floating or fullscreen.
    if (opening) {
      this.chatAnalytics.chatOpened()
    }
  }
  fetchAudioBlob = (url: string): Promise<Blob | null> => fetchDocument(url)

  downloadAttachment = async (file: ChatMessageFile): Promise<void> => {
    if (!file.downloadUrl) {
      return
    }
    const blob = await fetchDocument(file.downloadUrl)
    if (!blob) {
      return
    }
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
    URL.revokeObjectURL(url)
  }

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      this.inputDisabled = getBoolExpressionSignal(
        this.chatInputFlowxProps()?.disabled,
        this.chatInputTemplateConfig(),
        { logType: 'disabled' }
      )

      effect(() => {
        if (this.inputDisabled()) {
          this.attachments.set([])
          this.attachmentsUploadError.set(null)
          this.pendingVoice.set(null)
        }
      })
    })

    if (!getUrlQueryParamValue('chatSessionId')) {
      this.messages.set([
        {
          id: '1',
          content: this.chatConfig()?.welcomeMessage || 'Welcome to the chat!',
          sender: 'receiver',
          timestamp: new Date(),
          isWelcome: true,
        },
      ])
    }

    // Subscribe to conversations list pagination state
    conversationsListPaginationStore.stream$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((pagination: ConversationsListPaginationState) => {
        this.hasMoreSessions.set(pagination.hasMore)
        this.loadingMoreSessions.set(pagination.loading)
      })

    // Subscribe to chat store for session history
    combineLatest([chatStore.stream$, activeChatSessionIdStore.stream$])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([store, currentId]: [Record<string, ChatSessionState>, string | null]) => {
        this.chatSessions.set(
          Object.entries(store)
            .filter(([, session]) => !session.isLocalOnly && session.isSessionUpdated)
            .map(([sessionId, session]) => ({
              sessionId,
              summary: session.summary,
              messageCount: session.messageCount,
              active: sessionId === currentId,
              createdAt: session.createdAt,
            }))
        )

        // Seed the welcome message only when there are no sessions yet. This stream has
        // no distinctUntilChanged, so it re-emits on every store/active-session change
        // (e.g. an attachments upload re-activates the current session via
        // resolveChatSessionId). Setting [] here would wipe the rendered messages until
        // the chat$ subscription repopulates them — leave the message list to chat$.
        if (Object.keys(store).length === 0) {
          this.messages.set([
            {
              id: '1',
              content: this.chatConfig()?.welcomeMessage || 'Welcome to the chat!',
              sender: 'receiver',
              timestamp: new Date(),
              isWelcome: true,
            },
          ])
        }

        const existingChatSessionId = getUrlQueryParamValue('chatSessionId')
        if (!existingChatSessionId) {
          this.showSuggestedPrompts.set(true)
        }
      })

    const resDefId = getUiFlowType()?.chatWorkflow?.resDefId
    initChat({
      cache: false,
      source: {
        type: 'WORKFLOW' as ChatSourceType,
        id: resDefId,
      },
    }).then(({ sendMessage, chat$, loadMoreMessages, stopResponse }) => {
      this.handleSendMessage = sendMessage
      this.loadMoreMessages = loadMoreMessages
      this.handleStopResponse = stopResponse
      chat$
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(({ chatHistory, loading, hasMoreMessages, loadingMore, isLocalOnly }) => {
          this.hasMoreMessages.set(hasMoreMessages ?? false)
          this.loadingMore.set(loadingMore ?? false)
          this.reasoning.set(loading)
          this.showSuggestedPrompts.set(isLocalOnly)
          if (isLocalOnly) {
            this.agentSuggestions.set([])
            this.messages.set([
              {
                id: '1',
                content: this.chatConfig()?.welcomeMessage || 'Welcome to the chat!',
                sender: 'receiver',
                timestamp: new Date(),
                isWelcome: true,
              },
            ])
          } else {
            this.agentSuggestions.set(getLastAgentSuggestions(chatHistory ?? []))
            const mapped = chatHistory?.map((item) => normalizeChatHistoryMessage(item, new Map()))
            checkAndClearAngularPendingVoice(mapped, this.pendingVoice)
            this.messages.set(
              chatHistory?.map((item) =>
                normalizeChatHistoryMessage(item, this.#audioUrlCache, this.#messageTimestamps)
              )
            )
          }
        })
    })
  }

  ngAfterViewInit(): void {
    if (
      this.chatDisplayMode().includes(ChatDisplayType.FILL) &&
      this.chatDisplayOptions().style[this.chatHeaderDisplayMode()].heightType === 'fill'
    ) {
      const chatElement = this.#elementRef.nativeElement.querySelector('flx-chat') as HTMLElement
      if (chatElement) {
        setTimeout(() => {
          // TODO: temp solution until generic fill-height mechanism exists
          this.chatHeight.set(calculateFillHeight(chatElement))
        }, 0)
      }
    }
  }

  ngOnDestroy(): void {
    detachChat()
    cleanupAudioUrlCache(this.#audioUrlCache)
  }

  handleVoiceMessage(event: { blob: Blob; duration: number }): void {
    this.reasoning.set(true)
    handleAngularVoiceMessage(
      event,
      this.pendingVoice,
      this.#audioUrlCache,
      (msg, content) => this.#sendVoiceMessage(msg, content),
      () => this.reasoning.set(false)
    )
  }

  handleRetryVoice(): void {
    this.reasoning.set(true)
    retryAngularVoice(
      this.pendingVoice,
      this.#audioUrlCache,
      (msg, content) => this.#sendVoiceMessage(msg, content),
      () => this.reasoning.set(false)
    )
  }

  handleAttachmentsMessage(event: { message: string; files: File[] }): void {
    // Don't toggle `reasoning` here. The send button stays disabled during the
    // upload via the input's own in-flight guard, and the thinking indicator is
    // driven entirely by the chat stream's `loading` flag (same as text messages).
    // The stream renders the user message first, then flips loading on, so the
    // thinking bubble only appears after the sent message shows in the list.
    handleAngularAttachments(
      event,
      this.attachmentsUploadError,
      (msg, content) => {
        this.handleSendMessage(msg, content)
        this.chatAnalytics.textMessageSent()
      },
      (success) => {
        if (success) {
          this.attachments.set([])
        }
      }
    )
  }

  handleNewChat(): void {
    initChatSession()
    updateUrlWithQueryParam('chatSessionId', null)
    this.#messageTimestamps.clear()

    // Reset messages, reasoning, and switch to chat view
    const chatConfig = this.chatConfig()
    const welcomeMessage = chatConfig?.welcomeMessage
    this.messages.set(
      welcomeMessage
        ? [
            {
              id: 'welcome-message',
              content: welcomeMessage,
              sender: 'receiver',
              timestamp: new Date(),
              isWelcome: true,
            },
          ]
        : []
    )
    this.reasoning.set(false)
    this.agentSuggestions.set([])
    this.pendingVoice.set(null)
    this.attachments.set([])
    this.attachmentsUploadError.set(null)
    this.activeView.set('chat')
  }

  async handleOpenHistory(): Promise<void> {
    const resDefId = getUiFlowType()?.chatWorkflow?.resDefId
    if (resDefId) {
      await loadConversationsList(resDefId)
    }
    this.activeView.set('history')
  }

  async handleLoadMoreSessions(): Promise<void> {
    const resDefId = getUiFlowType()?.chatWorkflow?.resDefId
    if (resDefId) {
      await loadMoreConversations(resDefId)
    }
  }

  handleCloseHistory(): void {
    this.conversationsSearch.clearSearch()
    this.activeView.set('chat')
  }

  handleSelectSession(sessionId: string): void {
    setActiveChatSessionId(sessionId)
    updateUrlWithQueryParam('chatSessionId', sessionId)
    loadChatSessionHistory(sessionId)
    this.conversationsSearch.clearSearch()
    this.activeView.set('chat')
  }

  handleToggleFullscreen(): void {
    this.isMaximized.set(!this.isMaximized())
  }

  handleCloseChat(): void {
    this.isChatOpened.set(false)
  }
  handleLoadMore(): void {
    if (!this.hasMoreMessages() || this.loadingMore()) {
      return
    }
    this.loadMoreMessages?.()
  }

  handleFeedbackChange({
    messageId,
    value,
  }: {
    messageId: string
    value: ChatFeedbackValue | null
  }): void {
    this.messageFeedback.handleFeedbackChange(
      messageId,
      value,
      this.messages().find(
        (message) => message.messageId === messageId && message.sender === 'receiver'
      )?.feedback ?? null
    )
  }

  // Called once the recording is uploaded, so a failed upload sends no event until a retry succeeds.
  #sendVoiceMessage(message: string, content?: ChatMessageContent): void {
    this.handleSendMessage(message, content)
    this.chatAnalytics.voiceMessageSent()
  }
}
